import axiosInstance from "@/api/axiosInstance";
import {
  FetchEnrollmentsParams,
  FetchEnrollmentsResponse,
} from "./types";

/**
 * Fetch enrollments for a specific learning path
 * GET /api/learning-paths/{pathId}/enrollments
 *
 * @param pathId - ID of the learning path
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of enrollments with metadata
 *
 * @example
 * const enrollments = await fetchPathEnrollments(10, {
 *   status: 'active',
 *   page: 1,
 *   pageSize: 20,
 *   sortBy: 'enrolledAt',
 *   sortOrder: 'desc'
 * });
 */
export const fetchPathEnrollments = async (
  pathId: number,
  params: FetchEnrollmentsParams = {}
): Promise<FetchEnrollmentsResponse> => {
  try {
    console.log(`[fetchPathEnrollments] Fetching enrollments for path ${pathId} with params:`, params);
    const response = await axiosInstance.get<FetchEnrollmentsResponse>(
      `/api/learning-paths/${pathId}/enrollments`,
      { params }
    );
    console.log(`[fetchPathEnrollments] Response received:`, response.data);
    console.log(`[fetchPathEnrollments] Enrollments data:`, response.data.data);
    console.log(`[fetchPathEnrollments] Total enrollments:`, response.data.total);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching enrollments for path ${pathId}:`, error);
    console.error(`Error response:`, error.response);
    console.error(`Error data:`, error.response?.data);
    console.error(`Error data stringified:`, JSON.stringify(error.response?.data, null, 2));
    console.error(`Error status:`, error.response?.status);
    throw error;
  }
};

/**
 * Fetch enrollments for a specific employee
 * GET /api/employees/{employeeId}/enrollments
 *
 * @param employeeId - ID of the employee
 * @param params - Query parameters for filtering (partial params supported)
 * @returns List of enrollments for the employee
 *
 * @example
 * const enrollments = await fetchEmployeeEnrollments(101, {
 *   status: 'active',
 *   includeInactive: false
 * });
 */
export const fetchEmployeeEnrollments = async (
  employeeId: number,
  params: Partial<FetchEnrollmentsParams> = {}
): Promise<FetchEnrollmentsResponse> => {
  try {
    const response = await axiosInstance.get<FetchEnrollmentsResponse>(
      `/api/employees/${employeeId}/enrollments`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching enrollments for employee ${employeeId}:`, error);
    throw error;
  }
};
